import React, { useState, useEffect, useCallback } from 'react';
import { listDisputes, resolveDispute } from '../../services/disputeService';
import ConfirmDialog from '../ui/ConfirmDialog';
import { useToast } from '../../context/ToastContext';

const STATUS_TABS = [
  { id: 'open', label: 'Open' },
  { id: 'under_review', label: 'Under review' },
  { id: 'resolved', label: 'Resolved' }
];

const DisputeQueue = () => {
  const toast = useToast();
  const [disputes, setDisputes] = useState([]);
  const [notes, setNotes] = useState({});
  const [statusFilter, setStatusFilter] = useState('open');
  const [pendingResolution, setPendingResolution] = useState(null); // { id, decision }

  const refresh = useCallback(() => {
    listDisputes(statusFilter).then(setDisputes);
  }, [statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const finishResolve = async (misconductBy) => {
    const { id, decision } = pendingResolution;
    await resolveDispute(id, { decision, notes: notes[id] || '', misconductBy });
    setPendingResolution(null);
    toast.success('Dispute resolved');
    refresh();
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold">Disputes</h2>
      <div className="flex gap-2">
        {STATUS_TABS.map(tab => (
          <button key={tab.id} onClick={() => setStatusFilter(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-sm ${statusFilter === tab.id ? 'bg-black text-white' : 'bg-white'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      {disputes.map(d => (
        <div key={d._id} className="bg-white p-4 rounded-lg shadow space-y-2">
          <p className="font-semibold">Raised by {d.raisedBy}: {d.reason}</p>
          <p className="text-sm text-gray-600">{d.description}</p>
          <p className="text-sm">Order: {d.orderId?.massageType} — ₦{d.orderId?.pricing?.totalAmount?.toLocaleString()}</p>
          {d.status === 'resolved' && d.resolution && (
            <p className="text-sm bg-gray-50 p-2 rounded-lg">
              Resolved: {d.resolution.decision} — {d.resolution.notes}
            </p>
          )}
          {statusFilter !== 'resolved' && (
            <>
              <textarea
                placeholder="Resolution notes"
                value={notes[d._id] || ''}
                onChange={(e) => setNotes({ ...notes, [d._id]: e.target.value })}
                className="w-full p-2 border rounded-lg text-sm"
              />
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => setPendingResolution({ id: d._id, decision: 'release_to_therapist' })} className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm">
                  Release to therapist
                </button>
                <button onClick={() => setPendingResolution({ id: d._id, decision: 'refund_client' })} className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm">
                  Refund client
                </button>
                <button onClick={() => setPendingResolution({ id: d._id, decision: 'split' })} className="bg-yellow-600 text-white px-3 py-1.5 rounded-lg text-sm">
                  Split
                </button>
              </div>
            </>
          )}
        </div>
      ))}
      {disputes.length === 0 && <p className="text-gray-500">No {statusFilter.replace('_', ' ')} disputes.</p>}

      <ConfirmDialog
        open={!!pendingResolution}
        title="Was this the therapist's fault?"
        message="This affects the therapist's strike count. Choose based on the evidence provided."
        confirmLabel="Yes, misconduct"
        cancelLabel="No, not their fault"
        danger
        onConfirm={() => finishResolve('therapist')}
        onCancel={() => finishResolve(undefined)}
      />
    </div>
  );
};

export default DisputeQueue;
